import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  PROFILE_GROUP,
  PROFILE_METRIC,
  PROFILE_RESPONSE,
  TRENDS_METRIC,
  SECONDARY_BRANDS,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Emotional module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 */

export const emotionalModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    supportMarkets: true,
    module: "IMAGERY",
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    maximumAllowedMarkets: 25,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportBase: true,
    defaultBase: "GENPOP",
    supportedBases: ["GENPOP", "FAMILIAR"],
    supportDatePeriod: true,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["BUBBLE", "BAR", "PROFILE"],
    defaultVisualisationType: "BUBBLE",
    statTestingConfig: [
      { id: "POP", enabled: true, activeLabel: "Period-P" },
      {
        id: "YOY",
        enabled: true,
        activeLabel: "Year-Y",
        disableWhenDaysExceed: {
          days: 365,
          message:
            "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
        },
        disableForPeriods: {
          periods: ["P_W"],
          message:
            "Year-over-Year comparison is only available for Months & Quarters.",
        },
      },
      { id: "PTP", hidden: true },
    ],
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        maximumAllowedBrands: 1,
        apiGroupedBy: "SEGMENT",
        apiSendFilters: false,
        // Routes: [TREND, POINT_IN_TIME, PROFILE] — no PRE_POST
        supportedModes: ["TREND", "POINT_IN_TIME"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // Emotional By Segment TREND has TRENDS_METRIC but no RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "corporate",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "BRAND",
        apiSendFilters: true,
        // Routes: [TREND, POINT_IN_TIME, PRE_POST, PROFILE]
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // Emotional By Brand TREND has TRENDS_METRIC but no RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "corporate",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportVisualisationSwitch: true,
            supportedVisualisationTypes: [
              "BUBBLE",
              "BAR",
              "PROFILE",
              "DIFFERENTIATION",
              "PERCEPTUAL",
              "BUTTERFLY",
            ],
            // defaultVisualisationType intentionally omitted here —
            // inherits "BUBBLE" from the module-level config.
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DIFFERENTIATION: {
              config: {
                supportSecondaryBrands: true,
                apiSendCompetitors: true,
                maximumAllowedBrands: 1,
                minimumRequiredSecondaryBrands: 2,
                // No stat testing allowed for DIFFERENTIATION
                defaultBase: "FAMILIAR",
                supportBase: false,
                supportedBases: [],
                statTestingConfig: [],
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SECONDARY_BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                ],
              },
            },

            PERCEPTUAL: {
              config: {
                apiSendRestEntitiesAsCompetitors: true,
                minimumRequiredBrands: 3,
                // No stat testing allowed for PERCEPTUAL
                statTestingConfig: [],
                defaultBase: "FAMILIAR",
                supportBase: false,
                supportedBases: [],
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                ],
              },
            },

            BUTTERFLY: {
              config: {
                supportSecondaryBrands: true,
                apiSendCompetitors: true,
                maximumAllowedBrands: 1,
                maximumAllowedSecondaryBrands: 1,
                // No stat testing allowed for BUTTERFLY
                statTestingConfig: [],
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SECONDARY_BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            maximumAllowedBrands: 1,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              VISUALIZATION_TYPE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
            statTestingConfig: [
              { id: "POP", enabled: true, activeLabel: "Period-P" },
              {
                id: "YOY",
                enabled: false,
                activeLabel: "Year-Y",
                disabledMessage:
                  "Year-over-Year comparison is not available on this screen.",
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: true,
                enabled: false,
                activeLabel: "Peer-P",
                fallbackTo: "POP",
              },
            ],
          },
        },
      },
    },

    // ── By Market ────────────────────────────────────────────────────────────
    MARKET: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        isMarketsFilteredByBrand: true,
        isBrandsFilteredByMarket: false,
        maximumAllowedBrands: 1,
        apiGroupedBy: "MARKET",
        apiSendFilters: true,
        // Routes: [TREND, POINT_IN_TIME, PROFILE] — no PRE_POST
        supportedModes: ["TREND", "POINT_IN_TIME"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // Emotional By Market TREND has TRENDS_METRIC but no RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "corporate",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
      },
    },
  },
};
